// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useEffect, useState } from 'react';
import { Box, useTheme } from '@mui/system';
import useInterval from 'Components/hooks/useInterval';
import { useOktaAuth } from '@okta/okta-react';
// import Header from './Header';
import { Outlet } from 'react-router';
import { GetProjectsThunk } from 'Features/projectList/projectListThunk';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { GetOrgProfileThunk, GetUserProfileThunk, GetActivePromotionThunk } from 'Features/account/accountThunk';
import { GetImageryProvidersThunk } from 'Features/imagery/imageryThunk';
import { getUser } from 'Utils/localStorageService';
import { formatValue } from 'Utils/deliverables';
import { GetOrderStatusThunk } from 'Features/order/orderThunk';
import { GetTemplates } from 'Features/account/cadTemplates/cadTemplateThunk';
import ExpiredSessionModal from 'Components/ExpiredSessionModal';
import PromotionBanner from 'Components/PromotionBanner';
import LoadingSpinner from 'Components/LoadingSpinner';
import { GetNotificationsThunk } from './shellThunk';
import Header from './Header';
import stylesDef from './styles';
import { OktaAdminLoginThunk } from 'Features/auth/authThunk';

const Shell = () => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(true);
  const { token, resources } = useAppSelector(
    (state) => state.auth,
  );
  const { org } = useAppSelector(
    (state) => state.account,
  );
  const promotion = useAppSelector((state) => state.account.activePromotion);
  const user = getUser() ? JSON.parse(getUser()) : null;
  const opsTrainer = !_ADMIN_ && resources && 'opsTrainer' in resources;
  const { authState, oktaAuth } = _ADMIN_ && useOktaAuth();

  useEffect(() => {
    const loadData = async () => {
      // eslint-disable-next-line no-unused-expressions
      authState && !token && await dispatch(OktaAdminLoginThunk(authState.accessToken, authState.accessToken?.claims?.pricingAccess));
    };
    // eslint-disable-next-line no-unused-expressions
    _ADMIN_ && loadData();
  }, [authState, oktaAuth]);

  const loadData = async () => {
    const promiseArray = [
      // Still the first page of projects, though the dashboard grid no longer renders it: the
      // order-history selectors join orders to this list (orderSelectors.getProjectsList).
      // Removing it is its own ticket.
      dispatch(GetProjectsThunk()),
      dispatch(GetUserProfileThunk()),
      dispatch(GetOrgProfileThunk()),
      dispatch(GetNotificationsThunk()),
      dispatch(GetTemplates()),
    ];

    const resultArray = await Promise.all(promiseArray);
    const anyFailed = resultArray.some((r) => !r.success);
    setLoading(anyFailed);
  };

  useEffect(() => {
    if (_ADMIN_) {
      setLoading(false);
    } else {
      loadData();
    }
  }, []);

  useEffect(() => {
    if (!_ADMIN_ && org?.imageryProviders?.length > 0) {
      dispatch(GetImageryProvidersThunk());
    }
  }, [org?.imageryProviders]);

  // The server decides eligibility (PAYG only in this release), so every customer org asks once it is loaded.
  // Gated on the token too: on a refresh the persisted org can be in the store before the session is restored,
  // and asking then only earns a 401.
  useEffect(() => {
    if (!_ADMIN_ && org?._id && token) {
      dispatch(GetActivePromotionThunk());
    }
  }, [org?._id, token]);

  useEffect(() => {
    const script = document.createElement('script');

    script.src = '//js.hs-scripts.com/3434755.js';
    script.async = true;

    document.body.appendChild(script);
    // Start of Survicate (www.survicate.com) code
    if (window.location.hostname === 'cloud.airworks.io' && org !== null) {
      (function (opts) {
        opts.traits = {
          email: user.email,
          first_name: user.firstName,
          last_name: user.lastName,
          organization: org.companyName,
        };
      }(window._sva = window._sva || {}));
      (function (w) {
        const s = document.createElement('script');
        s.src = 'https://survey.survicate.com/workspaces/e446d987fb1a2dc0466a010b938591dd/web_surveys.js';
        s.async = true;
        const e = document.getElementsByTagName('script')[0];
        e.parentNode.insertBefore(s, e);
      }(window));
    }
    // End of Survicate code
    return () => {
      document.body.removeChild(script);
    };
  }, [org]);

  // useInterval(() => {
  //   if (!loading && !_ADMIN_) {
  //     GetUserProfileAction(true);
  //   }
  // }, 2000);

  // Call getOrders API every 30 minutes
  useInterval(() => {
    if (!loading && !_ADMIN_ && !opsTrainer) {
      dispatch(GetOrderStatusThunk());
    }
  }, 1800000);

  useInterval(() => {
    if (!loading && !_ADMIN_) {
      dispatch(GetNotificationsThunk());
    }
  }, 10000);

  return (
    <Box sx={styles.shellWrapper}>
      <LoadingSpinner loading={loading} />
      {token && !loading && (
        <>
          <Box sx={styles.headerWrapper}>
            <Header />
          </Box>
          {promotion && <PromotionBanner promotion={promotion} variant="page" dateFormat="MMMM D" />}
          <Box sx={styles.contentWrapper}>
            <Outlet />
          </Box>
        </>
      )}
      <ExpiredSessionModal />
    </Box>
  );
};

export default Shell;
